import React, { useEffect, useState, useSyncExternalStore } from "react";
import {
  ArrowRight,
  ArrowLeft,
  PresentationChart,
  FileText,
  EnvelopeSimple,
  Lightbulb,
  BookBookmark,
  Scales,
  CalendarStar,
  Sliders,
  X,
} from "@phosphor-icons/react";

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TOTAL_SLIDES = 3;

/* ─── Mobile detection (< 640px, matches Tailwind sm breakpoint) ─── */

const mobileQuery = "(max-width: 639px)";

function subscribeMobile(cb: () => void) {
  const mql = window.matchMedia(mobileQuery);
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
}

function getSnapshotMobile() {
  return window.matchMedia(mobileQuery).matches;
}

function getServerSnapshotMobile() {
  return false;
}

function useIsMobile() {
  return useSyncExternalStore(subscribeMobile, getSnapshotMobile, getServerSnapshotMobile);
}

export default function WelcomeModal({ isOpen, onClose }: WelcomeModalProps) {
  const isMobile = useIsMobile();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setCurrentSlide(0);
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isMobile, onClose]);

  if (isMobile) {
    return <MobileWelcomeModal isOpen={isOpen} onClose={onClose} />;
  }

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentSlide < TOTAL_SLIDES - 1) {
      setCurrentSlide(s => s + 1);
    } else {
      handleClose();
    }
  };

  const handleBack = () => {
    if (currentSlide > 0) {
      setCurrentSlide(s => s - 1);
    }
  };

  const handleClose = () => {
    setCurrentSlide(0);
    onClose();
  };

  const nextLabel = (() => {
    switch (currentSlide) {
      case 0:
        return "Next: Find and Q&A Past Deals";
      case 1:
        return "Next: Draft Offer Letter";
      case 2:
        return "Finish";
      default:
        return "Next";
    }
  })();

  return (
    <>
      {/* Modal + Backdrop (single layer so click-outside works reliably) */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: "rgba(52, 52, 52, 0.80)" }}
        onMouseDown={handleClose}
      >
        <div
          className="bg-white rounded-[10px] w-full max-w-[900px] overflow-hidden"
          style={{ boxShadow: "0px 0px 50px 0px rgba(0,0,0,0.25)" }}
          role="dialog"
          aria-modal="true"
          aria-label="Welcome to DealOS"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-8 py-4">
            <a
              href="https://cvccapitalpartners.sharepoint.com/sites/ai-cvc"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm font-cvcBody hover:underline"
              style={{ color: "#234E81" }}
            >
              AI @ CVC
              <ArrowRight size={14} />
            </a>
            <span
              className="text-[18px] font-cvcBody"
              style={{ color: "#234E81" }}
            >
              DealOS
            </span>
            <a
              href="mailto:ai@cvc.com?subject=DealOS%20Feedback"
              className="flex items-center gap-1.5 text-sm font-cvcBody hover:underline"
              style={{ color: "#234E81" }}
            >
              Submit feedback
              <EnvelopeSimple size={16} />
            </a>
          </div>

          {/* Separator */}
          <div className="border-t border-[rgba(52,52,52,0.15)]" />

          {/* Content Area */}
          <div style={{ backgroundColor: "#F9F8F3" }} className="min-h-[525px]">
            {currentSlide === 0 && <SlideWelcome />}
            {currentSlide === 1 && <SlideFindDeals />}
            {currentSlide === 2 && <SlideDraftOfferLetter />}
          </div>

          {/* Separator */}
          <div className="border-t border-[rgba(52,52,52,0.15)]" />

          {/* Footer */}
          <div className="flex items-center justify-between px-8 py-4">
            {/* Back button */}
            <div className="w-[240px]">
              {currentSlide > 0 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-2 px-4 py-2 rounded-[5px] border border-[rgba(35,78,129,0.3)] hover:border-[#234E81] text-sm font-cvcBody hover:bg-gray-50 transition-colors"
                  style={{ color: "#234E81" }}
                >
                  <ArrowLeft size={14} />
                  Back
                </button>
              )}
            </div>

            {/* Pagination dots */}
            <div className="flex items-center gap-2">
              {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`rounded-full transition-colors ${
                    i === currentSlide
                      ? "w-3 h-3"
                      : "w-3 h-3 border border-[rgba(35,78,129,0.3)]"
                  }`}
                  style={
                    i === currentSlide
                      ? { backgroundColor: "#234E81" }
                      : { backgroundColor: "transparent" }
                  }
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            {/* Next button */}
            <div className="w-[240px] flex justify-end">
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-2 px-4 py-2 rounded-[5px] border border-[rgba(35,78,129,0.3)] hover:border-[#234E81] text-sm font-cvcBody hover:bg-gray-50 transition-colors whitespace-nowrap"
                style={{ color: "#234E81" }}
              >
                {nextLabel}
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Mobile full-screen modal ─── */

function MobileWelcomeModal({ isOpen, onClose }: WelcomeModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 flex-shrink-0">
        <span
          className="text-base font-cvcBody"
          style={{ color: "#234E81" }}
        >
          DealOS
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1"
          aria-label="Close"
        >
          <X size={20} color="#234E81" />
        </button>
      </div>

      <div className="border-t border-[rgba(52,52,52,0.15)]" />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto" style={{ backgroundColor: "#F9F8F3" }}>
        <div className="flex flex-col items-center px-5 py-8">
          <h2
            className="font-cvcDisplay font-medium text-2xl leading-8 text-center mb-3"
            style={{ color: "#234E81" }}
          >
            Welcome to DealOS
          </h2>

          <p className="font-cvcBody text-sm leading-5 text-center mb-8 text-cvc-text">
            CVC&apos;s proprietary AI platform for deal intelligence,
            with more powerful features on the way.
          </p>

          {/* Feature: Past Deals */}
          <div className="w-full bg-white border border-[rgba(52,52,52,0.15)] rounded-[10px] p-5 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <PresentationChart size={22} color="#234E81" />
              <span className="font-cvcBody text-sm font-medium" style={{ color: "#234E81" }}>
                Find and Q&A Past Deals
              </span>
              <BetaBadge />
            </div>
            <p className="font-cvcBody text-sm leading-5 text-cvc-text mb-3">
              Search and interrogate CVC&apos;s library of past deals,
              IC papers and DD docs.
            </p>
            <ul className="font-cvcBody text-xs leading-5 text-cvc-text space-y-1 pl-4 list-disc">
              <li>Learn from CVC experience across 1000s of IC memos and CDDs</li>
              <li>Benchmark valuations, multiples and deal structures</li>
              <li>Find past IC questions and how memos handled them</li>
              <li>Pull credentials for marketing materials and offer letters</li>
            </ul>
          </div>

          {/* Feature: Offer Letter */}
          <div className="w-full bg-white border border-[rgba(52,52,52,0.15)] rounded-[10px] p-5 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={22} color="#234E81" />
              <span className="font-cvcBody text-sm font-medium" style={{ color: "#234E81" }}>
                Draft Offer Letter
              </span>
              <BetaBadge />
            </div>
            <p className="font-cvcBody text-sm leading-5 text-cvc-text">
              Upload a process letter and supporting docs to generate
              a CVC-formatted offer letter you can review, edit and download.
            </p>
          </div>

          {/* Feedback link */}
          <a
            href="mailto:ai@cvc.com?subject=DealOS%20Feedback"
            className="flex items-center gap-1.5 text-xs font-cvcBody hover:underline mt-2 mb-4"
            style={{ color: "#234E81" }}
          >
            Submit feedback
            <EnvelopeSimple size={14} />
          </a>
        </div>
      </div>

      {/* Get started button */}
      <div className="flex-shrink-0 px-5 py-4 bg-white border-t border-[rgba(52,52,52,0.15)]">
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 rounded-[8px] text-base font-cvcBody font-medium text-white transition-colors"
          style={{ backgroundColor: "#234E81" }}
        >
          Get started
        </button>
      </div>
    </div>
  );
}

/* ─── Slide 1: Welcome Overview ─── */

function SlideWelcome() {
  return (
    <div className="flex flex-col items-center px-8 py-10">
      <h2
        className="font-cvcDisplay font-medium text-[34px] leading-[40px] text-center max-w-[560px] mb-4"
        style={{ color: "#234E81" }}
      >
        Welcome to DealOS, CVC&apos;s proprietary AI platform.
      </h2>

      <p className="font-cvcBody text-base leading-6 text-center max-w-[560px] mb-8 text-cvc-text">
        Right now the platform can help you search and interrogate CVC&apos;s
        library of past deals, IC papers and DD docs, with more powerful features
        on the way.
      </p>

      <p className="text-xs font-cvcBody uppercase tracking-wider text-cvc-text opacity-40 mb-6">
        DealOS Features
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-[680px]">
        <FeatureCard
          icon={<PresentationChart size={24} color="#234E81" />}
          title="Find and Q&A Past Deals"
          description="Search and interrogate CVC's library of past deals, IC papers and DD docs."
          isBeta
        />
        <FeatureCard
          icon={<FileText size={24} color="#234E81" />}
          title="Draft Offer Letter"
          description="Create a draft by uploading a process letter and other notes, pre-PIRs, etc."
          isBeta
        />
      </div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
  isBeta,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  isBeta?: boolean;
}) {
  return (
    <div className="bg-white border border-[rgba(52,52,52,0.15)] rounded-[5px] p-5">
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-cvcBody text-sm" style={{ color: "#234E81" }}>
          {title}
        </span>
        {isBeta && <BetaBadge />}
      </div>
      <div className="border-b border-gray-200 my-2" />
      <p className="font-cvcBody text-xs leading-[18px] text-cvc-text">
        {description}
      </p>
    </div>
  );
}

function BetaBadge() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-cvcBody bg-cvc-green/20 text-cvc-green uppercase">
      BETA
    </span>
  );
}

/* ─── Slide 2: Find and Q&A Past Deals ─── */

function SlideFindDeals() {
  return (
    <div className="px-8 py-8">
      {/* Title */}
      <div className="flex items-center gap-3 mb-6">
        <PresentationChart size={36} color="#234E81" />
        <h2
          className="font-cvcDisplay font-medium text-[34px] leading-[40px]"
          style={{ color: "#234E81" }}
        >
          Find and Q&A Past Deals
        </h2>
        <BetaBadge />
      </div>

      {/* Top row: description + 2 capability cards */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-[rgba(52,52,52,0.03)] rounded-[10px] p-5 flex items-start">
          <p className="font-cvcBody text-base leading-6 text-cvc-text">
            Search and interrogate CVC&apos;s library of past deals, IC papers
            and DD docs.
          </p>
        </div>
        <CapabilityCard
          icon={<Lightbulb size={20} color="#234E81" />}
          title="Learn from CVC experience"
          description="Leverage 1000s of IC memos and CDDs to get up to speed on a new area."
        />
        <CapabilityCard
          icon={<BookBookmark size={20} color="#234E81" />}
          title="Prepare for IC"
          description="Find past IC questions and see how memos handled them."
        />
      </div>

      {/* Bottom row: 3 capability cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <CapabilityCard
          icon={<Scales size={20} color="#234E81" />}
          title="Benchmark your deal against CVC history"
          description="Compare valuations, multiples, and deal structures across CVC's portfolio history."
        />
        <CapabilityCard
          icon={<CalendarStar size={20} color="#234E81" />}
          title="Pull credentials for marketing materials"
          description="Quickly assemble track record for founder-facing decks / offer letters."
        />
        <CapabilityCard
          icon={<Sliders size={20} color="#234E81" />}
          title="Learn from execution and value creation"
          description="Understand how VCP levers were applied and what worked."
        />
      </div>

      {/* Data available by strategy */}
      <p className="font-cvcBody text-base leading-[18px] text-cvc-text mb-3">
        Data available by strategy:
      </p>
      <div className="grid grid-cols-6 gap-3">
        <div className="col-span-2">
          <StrategyColumn
            name="Private Equity"
            deals="All. Reached PIR after 2011 (some masked)."
            docs="Published IC papers / CDDs."
          />
        </div>
        <StrategyColumn
          name="Private Credit"
          deals="Coming soon"
          docs="Coming soon"
          dimmed
        />
        <StrategyColumn name="Liquid Credit" deals="None" docs="None" dimmed />
        <StrategyColumn name="Infrastructure" deals="None" docs="None" dimmed />
        <StrategyColumn name="Secondaries" deals="None" docs="None" dimmed />
      </div>
    </div>
  );
}

function CapabilityCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white border border-[rgba(52,52,52,0.15)] rounded-[5px] p-4">
      <div className="flex items-start gap-2">
        {icon}
        <span
          className="font-cvcBody text-sm leading-[21px]"
          style={{ color: "#234E81" }}
        >
          {title}
        </span>
      </div>
      <div className="border-b border-gray-200 my-2" />
      <p className="font-cvcBody text-xs leading-[18px] text-cvc-text">
        {description}
      </p>
    </div>
  );
}

function StrategyColumn({
  name,
  deals,
  docs,
  dimmed,
}: {
  name: string;
  deals: string;
  docs: string;
  dimmed?: boolean;
}) {
  return (
    <div className="bg-[rgba(52,52,52,0.03)] rounded-[5px] p-3">
      <p className="font-cvcBody text-[11px] uppercase text-cvc-text mb-1">
        {name}
      </p>
      <div className={`font-cvcBody text-[11px] leading-4 text-cvc-text ${dimmed ? "opacity-50" : ""}`}>
        <p>
          <span className="font-normal">Deals:</span>{" "}
          <span>{deals}</span>
        </p>
        <p>
          <span className="font-normal">Docs:</span>{" "}
          <span>{docs}</span>
        </p>
      </div>
    </div>
  );
}

/* ─── Slide 3: Draft Offer Letter ─── */

function SlideDraftOfferLetter() {
  const steps = [
    "Define which company or deal your letter is for.",
    "Upload process letter, past offer letters and supplementary docs.",
    "Preview a draft outline.",
    "View and edit your offer letter.",
    "Download your letter as a CVC formatted docx.",
  ];

  return (
    <div className="px-8 py-8">
      {/* Title */}
      <div className="flex items-center gap-3 mb-6">
        <FileText size={36} color="#234E81" />
        <h2
          className="font-cvcDisplay font-medium text-[34px] leading-[40px]"
          style={{ color: "#234E81" }}
        >
          Draft Offer Letter
        </h2>
        <BetaBadge />
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-[266px_1fr] gap-6">
        {/* Description card */}
        <div className="bg-[rgba(52,52,52,0.03)] rounded-[10px] p-5">
          <p className="font-cvcBody text-base leading-6 text-cvc-text">
            Write and edit an offer letter for a target company based on input
            documents like process letter, Pre-PIR, CIM and previous offer
            letters from your team.
          </p>
        </div>

        {/* Steps */}
        <div className="flex flex-col gap-3">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex items-center gap-4 bg-white border border-[rgba(52,52,52,0.15)] rounded-[5px] px-5 py-3"
            >
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#234E81] text-white text-sm font-cvcBody flex items-center justify-center">
                {index + 1}
              </span>
              <p className="font-cvcBody text-sm" style={{ color: "#234E81" }}>{step}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
